import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

// hooks / apis
import useInfiniteScroll from 'helpers/hooks/useInfiniteScroll';
import useDebounce from 'helpers/hooks/useDebounce';
import { useUsersInfiniteQuery } from 'api/account';

// material-ui
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

// icons
import Search from '@material-ui/icons/Search';

// components
import TextField from 'components/TextField';

type Props = {
  opened: boolean;
  selectedStaffMember: User | null;
  toggleOpened: () => void;
  toggleClosed: () => void;
  handleValueChange: (_: ChangeEvent<{}>, val: string | User | null) => void;
};

const StaffMemberSelector: FC<Props> = ({
  opened,
  selectedStaffMember,
  toggleOpened,
  toggleClosed,
  handleValueChange,
}) => {
  const classes = useStyles();

  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<User[]>([]);

  const staffMemberSearchValue = useDebounce(searchValue, 600);

  const {
    data,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useUsersInfiniteQuery(
    {
      search: staffMemberSearchValue,
      isSystemAdmin: 'false',
      isStaff: 'true',
    },
    { enabled: opened }
  );

  const users: User[] = useMemo(() => (data?.pages ? data.pages.flat() : []), [
    data,
  ]);

  const loading = isFetching || isFetchingNextPage;
  const [scrolledToBottom, scrollRef] = useInfiniteScroll(loading, 8);

  useEffect(() => {
    if (hasNextPage && scrolledToBottom) fetchNextPage();
  }, [hasNextPage, fetchNextPage, scrolledToBottom]);

  useEffect(() => {
    setOptions(() => (opened ? users : []));
  }, [opened, users]);

  const handleSearchChange = useCallback(
    (_: ChangeEvent<{}>, val: string) => setSearchValue(val),
    []
  );

  return (
    <Autocomplete
      freeSolo
      open={opened}
      onOpen={toggleOpened}
      onClose={toggleClosed}
      options={options}
      value={selectedStaffMember}
      classes={{ listbox: classes.listBox }}
      onChange={handleValueChange}
      loading={loading}
      onInputChange={handleSearchChange}
      getOptionLabel={option => `${option.firstName} ${option.lastName}`}
      filterOptions={() => users}
      renderOption={renderOption}
      ListboxProps={{
        role: 'listbox',
        ref: scrollRef,
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder='Search Users'
          variant='outlined'
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position='start'>
                <Search />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

const renderOption = (option: User) => {
  return (
    <Grid container direction='column'>
      <Typography>{`${option.firstName} ${option.lastName}`}</Typography>
      <Typography color='textSecondary'>{option.primaryEmail}</Typography>
    </Grid>
  );
};

const useStyles = makeStyles({
  listBox: {
    maxHeight: '300px',
  },
});

export default StaffMemberSelector;
